import { CreateUserRequest, TenantRole } from '@/api/generatedApi';
import { ApiErrorAlert } from '@/components/Errors/ApiErrorAlert';
import { useCreateUser } from '@/hooks/api/users/useCreateUser';
import { ApiErrorDisplay, AxiosApiError, getApiError } from '@/types/apiError';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  useTheme,
} from '@mui/material';
import { useState } from 'react';
import { TitleText } from '@/components/TitleText';

export const AddUserModal = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
  const theme = useTheme();
  const { createUser, isCreating } = useCreateUser();
  const [formData, setFormData] = useState<CreateUserRequest>({
    email: '',
    tenantRole: TenantRole.Author,
  });
  const [error, setError] = useState<ApiErrorDisplay | undefined>();

  const handleClose = () => {
    setFormData({ email: '', tenantRole: TenantRole.Author });
    onClose();
    setTimeout(() => {
      setError(undefined);
    }, theme.transitions.duration.leavingScreen);
  };

  const handleError = (errorCreatingUserError: AxiosApiError) => {
    setError(getApiError(errorCreatingUserError, 'Failed to create user'));
  };

  const handleSubmit = () =>
    createUser(formData, {
      onSuccess: handleClose,
      onError: handleError,
    });

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      fullWidth
      maxWidth='xs'
      slotProps={{
        paper: {
          sx: {
            borderRadius: '12px',
          },
        },
      }}
    >
      <TitleText sx={{ padding: '24px 8px 0 24px' }}>Add User</TitleText>
      <Box sx={{ padding: '8px 24px 24px 24px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
        <ApiErrorAlert error={error} />
        <TextField
          label='Email'
          value={formData.email}
          onChange={event => setFormData({ ...formData, email: event.target.value })}
          fullWidth
          required
          type='email'
        />
        <FormControl fullWidth required>
          <InputLabel id='role-label'>Role</InputLabel>
          <Select
            labelId='role-label'
            value={formData.tenantRole}
            label='Role'
            onChange={event => setFormData({ ...formData, tenantRole: event.target.value as TenantRole })}
          >
            <MenuItem value={TenantRole.Admin}>Admin</MenuItem>
            <MenuItem value={TenantRole.Author}>Author</MenuItem>
            <MenuItem value={TenantRole.Viewer}>Viewer</MenuItem>
            <MenuItem value={TenantRole.Search}>Search</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <DialogActions sx={{ padding: '0 24px 24px 24px' }}>
        <Button
          sx={{ textTransform: 'none', borderRadius: '12px', padding: '8px 16px' }}
          onClick={handleClose}
          disabled={isCreating}
        >
          Cancel
        </Button>
        <Button
          sx={{ textTransform: 'none', borderRadius: '12px', padding: '8px 16px' }}
          onClick={handleSubmit}
          variant='contained'
          color='primary'
          disabled={isCreating || !formData.email.trim()}
        >
          {isCreating ? 'Adding...' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
